import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { EmergencyFundData, FormValues, UpsertDTO } from './EmergencyFund.meta';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { useEmergencyFund } from './useEmergencyFund';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';
import { useGoalFormDisabled } from '../../hooks/useGoalFormDisabled';

const FORM_ID = 'ProfileGoals--EmergencyFund';

interface Props extends GoalComponentProps {
  goalData: EmergencyFundData;
}

export const EmergencyFund = ({
  clientId,
  goalData,
  yearsOptions,
  assetBucketingIndices,
  ...rest
}: Props) => {
  const { fundSize, fundedDate, fundedDateOptions } = useEmergencyFund({
    goalData,
    yearsOptions,
  });

  const readOnly = useGoalFormDisabled();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      fundSize,
      fundedDate,
      assetBucketingIndices,
    },
    disabled: readOnly,
  });

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: '/Consumer/GoalUpdate/SaveEmergencyFundGoal' });

  const onSubmit = async (values: FormValues) => {
    try {
      const upsertDTO: UpsertDTO = values;

      await upsertGoal(upsertDTO);

      rest.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal clientId={clientId} formId={FORM_ID} isLoading={isUpsertingGoal} {...rest}>
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        display="grid"
        css={`
          grid-template-columns: 1fr;
          gap: 24px;
        `}
        aria-label="Emergency Fund goal form"
      >
        <Controller
          control={control}
          name="fundSize"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Target Amount"
              leftIcon="far fa-dollar-sign"
              decimalScale={2}
              allowNegative={false}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              tooltip="Financial experts generally suggest having between 3 and 9 months' worth of essential living expenses set aside for emergencies (e.g., loss of a job, unexpected healthcare expenses, or home and car repairs)."
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="fundedDate"
          render={({ field: { value, onChange, disabled } }) => (
            <Select
              label="Target Date"
              value={value ? ({ value, label: value.toString() } as any) : null}
              options={fundedDateOptions}
              onChange={opt => onChange(opt?.value)}
              tooltip="This is the date by which you want to have your full Target Amount set aside."
              isPortal
              disabled={disabled}
            />
          )}
        />

        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
